import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";
import { UserContext } from "../contexts/UserContext";
import ProspeccionToqueModal       from "./ProspeccionToqueModal";
import ProspeccionHitoModal        from "./ProspeccionHitoModal";
import ProspeccionFichaModal       from "./ProspeccionFichaModal";
import ProspeccionCambioEstadoModal from "./ProspeccionCambioEstadoModal";
import ProspeccionGenerarSolicitudModal from "./ProspeccionGenerarSolicitudModal";
import ProspeccionDescartarModal   from "./ProspeccionDescartarModal";
import ProspeccionContactoModal    from "./ProspeccionContactoModal";
import ProspeccionEditarModal      from "./ProspeccionEditarModal";
import ProspeccionTareaModal       from "./ProspeccionTareaModal";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

// ─── Constantes ──────────────────────────────────────────────────────────────
const ESTADOS_CARA_A = ["sin_tocar", "esperando_respuesta", "frio"];
const ESTADOS_CARA_B = ["en_dialogo", "en_pausa"];
const DIAS_ESPERA_UMBRAL = 2;

const ESTADO_LABEL = {
  sin_tocar:           "Sin tocar",
  esperando_respuesta: "Esperando respuesta",
  frio:                "Frío",
  en_dialogo:          "En diálogo",
  en_pausa:            "En pausa",
};

const ESTADO_BADGE = {
  sin_tocar:           { bg: "#f3f4f6", color: "#6B7280" },
  esperando_respuesta: { bg: "#fef3c7", color: "#a16207" },
  frio:                { bg: "#e2e8f0", color: "#475569" },
  en_dialogo:          { bg: "#dbeafe", color: "#1d4ed8" },
  en_pausa:            { bg: "#f3f4f6", color: "#374151" },
};

const CANAL_LABEL = {
  instagram: "Instagram",
  linkedin:  "LinkedIn",
  whatsapp:  "WhatsApp",
  email:     "Email",
  llamada:   "Llamada",
  otro:      "Otro",
};

const TIPO_LABEL = {
  toque:     "Toque",
  respuesta: "Respuesta",
  hito:      "Hito",
};

const formatFechaHora = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

// ─── Componente principal ────────────────────────────────────────────────────
const ProspeccionDetalle = () => {
  const { id } = useParams();
  const history = useHistory();
  const { user } = useContext(UserContext);
  const userId = user?.id || JSON.parse(localStorage.getItem("user") || "{}").id;

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  // Modales
  const [modalToque,    setModalToque]    = useState(null); // 'toque' | 'respuesta' | null
  const [modalHito,     setModalHito]     = useState(false);
  const [modalFicha,    setModalFicha]    = useState(false);
  const [modalCambio,   setModalCambio]   = useState(null); // estado destino o null
  const [modalSolicitud,setModalSolicitud]= useState(false);
  const [modalDescart,  setModalDescart]  = useState(false);
  const [modalContacto, setModalContacto] = useState(null);
  const [modalEditar,   setModalEditar]   = useState(false);
  const [modalTarea,    setModalTarea]    = useState(false);

  const fetchDetalle = useCallback(() => {
    setLoading(true);
    const fd = new FormData();
    fd.append("id", id);
    axios.post(`${BASE}/prospectos_detalle.php`, fd)
      .then((r) => {
        if (r.data?.success) setData(r.data);
        else toast.error(r.data?.mensaje || "Error al cargar el prospecto");
      })
      .catch(() => toast.error("Error de conexión"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchDetalle(); }, [fetchDetalle]);

  const eliminarContacto = async (cid) => {
    if (!window.confirm("¿Eliminar este contacto?")) return;
    try {
      const fd = new FormData();
      fd.append("id", cid);
      const r = await axios.post(`${BASE}/prospectos_contactos_eliminar.php`, fd);
      if (r.data?.success) { toast.success("Contacto eliminado"); fetchDetalle(); }
      else toast.error(r.data?.mensaje || "Error");
    } catch { toast.error("Error de conexión"); }
  };

  if (loading) return <p style={{ padding: 40, textAlign: "center", color: "#aaa" }}>Cargando…</p>;
  if (!data || !data.prospecto) return <p style={{ padding: 40, textAlign: "center", color: "#dc2626" }}>Prospecto no encontrado.</p>;

  const { prospecto, contactos, interacciones } = data;
  // Fallback de estado (en caso de prospectos viejos no migrados)
  const estado = prospecto.estado || (
    prospecto.etapa === "Identificado" ? "sin_tocar" :
    prospecto.etapa === "Contactado"   ? "esperando_respuesta" :
    prospecto.etapa === "Conversacion" ? "en_dialogo" :
    prospecto.etapa === "Nurturing"    ? "en_pausa" : "sin_tocar"
  );
  const esCaraA = ESTADOS_CARA_A.includes(estado);

  // Toques separados de hitos para los timelines
  const toques = interacciones.filter((i) => i.tipo === "toque" || i.tipo === "respuesta");
  const hitos  = interacciones.filter((i) => i.tipo === "hito");

  // Días esperando (solo si esperando_respuesta)
  let diasEsperando = null;
  if (estado === "esperando_respuesta") {
    const ultimoToque = toques.find((i) => i.tipo === "toque");
    if (ultimoToque) {
      diasEsperando = Math.floor((Date.now() - new Date(ultimoToque.fecha).getTime()) / 86400000);
    }
  }
  const enAlerta = diasEsperando !== null && diasEsperando > DIAS_ESPERA_UMBRAL;

  // Ubicación formateada
  const ubicTexto = (() => {
    const partes = [
      prospecto.calle && `${prospecto.calle}${prospecto.numero ? " " + prospecto.numero : ""}`,
      prospecto.ciudad, prospecto.provincia, prospecto.pais,
    ].filter(Boolean);
    return partes.length ? partes.join(", ") : (prospecto.ubicacion || "—");
  })();

  const badge = ESTADO_BADGE[estado];

  return (
    <div style={{ minHeight: "100vh", background: "#f7f4f6", padding: "28px 20px", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
    <div style={{ maxWidth: 980, margin: "0 auto" }}>
      <button onClick={() => history.push("/dashboard/crm/prospeccion")}
        style={{ background: "none", border: "none", color: "#9f0059", fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 14 }}>
        ← Volver al embudo
      </button>

      {/* Cabecera */}
      <div style={S.cabecera}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#121212" }}>{prospecto.empresa}</h1>
            <p style={{ margin: "4px 0 0", color: "#888", fontSize: 13 }}>
              {prospecto.industria || "—"} · {ubicTexto}
              {prospecto.sitio_web && <> · <a href={prospecto.sitio_web.startsWith("http") ? prospecto.sitio_web : "https://"+prospecto.sitio_web} target="_blank" rel="noreferrer" style={{ color: "#9f0059" }}>{prospecto.sitio_web}</a></>}
              {prospecto.instagram && <> · <a href={prospecto.instagram.startsWith("http") ? prospecto.instagram : "https://instagram.com/" + prospecto.instagram.replace("@","")} target="_blank" rel="noreferrer" style={{ color: "#9f0059" }}>📷 IG</a></>}
              {prospecto.linkedin  && <> · <a href={prospecto.linkedin.startsWith("http")  ? prospecto.linkedin  : "https://" + prospecto.linkedin}  target="_blank" rel="noreferrer" style={{ color: "#9f0059" }}>💼 LI</a></>}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setModalEditar(true)} style={S.btnLink}>✎ Editar datos</button>
            <span style={{ background: badge.bg, color: badge.color, padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
              {ESTADO_LABEL[estado]}
            </span>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginTop: 16 }}>
          <Field label="Segmento"   value={prospecto.segmento || prospecto.segmento_prospeccion} />
          <Field label="Origen"     value={prospecto.origen} />
          <Field label="Asignado a" value={prospecto.usuario_asignado || "—"} />
        </div>
        {prospecto.senal_calificacion && (
          <div style={{ marginTop: 14, padding: "10px 14px", background: "#fef3c7", borderRadius: 8, borderLeft: "3px solid #f59e0b" }}>
            <div style={S.miniLabel}>Señal de calificación</div>
            <div style={{ fontSize: 13, color: "#78350f" }}>{prospecto.senal_calificacion}</div>
          </div>
        )}
        {prospecto.notas && (
          <div style={{ marginTop: 12 }}>
            <div style={S.miniLabel}>Notas</div>
            <div style={{ fontSize: 13, color: "#444", whiteSpace: "pre-wrap" }}>{prospecto.notas}</div>
          </div>
        )}
      </div>

      {/* ═══ CARA A: log de toques ═══ */}
      {esCaraA && (
        <>
          {/* Banner alerta esperando */}
          {enAlerta && (
            <div style={{
              background: "#fef3c7", border: "1.5px solid #fbbf24", borderRadius: 10,
              padding: "16px 20px", marginBottom: 16,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>
                ⏳ Esperando respuesta hace {diasEsperando} días
              </div>
              <div style={{ fontSize: 12, color: "#78350f", marginBottom: 12 }}>
                ¿Qué querés hacer?
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => setModalToque("respuesta")} style={{ ...S.btnPrimaryAmber, background: "#1d4ed8", borderColor: "#1d4ed8" }}>✓ Me respondieron</button>
                <button onClick={() => setModalToque("toque")}     style={S.btnPrimaryAmber}>↻ Insistir por otro lado</button>
                <button onClick={() => setModalCambio("frio")}     style={S.btnSecondaryAmber}>❄ Dejarlo enfriar</button>
              </div>
            </div>
          )}

          {/* Botón de toque */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, justifyContent: "flex-end", flexWrap: "wrap" }}>
            {estado === "sin_tocar" ? (
              <button onClick={() => setModalToque("toque")} style={S.btnPrimary}>📤 Registrar primer toque</button>
            ) : estado === "esperando_respuesta" && !enAlerta ? (
              <>
                <button onClick={() => setModalToque("respuesta")} style={S.btnPrimary}>✓ Me respondieron</button>
                <button onClick={() => setModalToque("toque")} style={S.btnSecondary}>📤 Registrar otro toque</button>
              </>
            ) : estado === "frio" ? (
              <button onClick={() => setModalToque("toque")} style={S.btnSecondary}>📤 Retomar (registrar toque)</button>
            ) : null}
            <button onClick={() => setModalTarea(true)} style={S.btnSecondary}>📅 Crear tarea</button>
          </div>

          {/* Timeline de toques */}
          <Section title={`Toques (${toques.length})`}>
            {toques.length === 0 ? (
              <p style={S.empty}>Todavía no registraste ningún toque.</p>
            ) : (
              toques.map((i) => (
                <div key={i.id} style={S.item}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#222" }}>
                      {i.tipo === "respuesta" ? "💬 Respondieron" : "📤 Toque"}
                      {i.toque_numero && i.tipo === "toque" && <span style={{ color: "#9f0059", marginLeft: 8 }}>#{i.toque_numero}</span>}
                      {i.canal && <span style={{ color: "#888", marginLeft: 8, fontWeight: 400 }}>· {CANAL_LABEL[i.canal] || i.canal}</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {i.usuario_nombre && <span style={{ fontSize: 11, color: "#9f0059", fontWeight: 600 }}>{i.usuario_nombre}</span>}
                      <span style={{ fontSize: 11, color: "#888" }}>{formatFechaHora(i.fecha)}</span>
                    </div>
                  </div>
                  {i.contacto_nombre && (
                    <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>→ {i.contacto_nombre}</div>
                  )}
                  {i.resumen && <div style={{ fontSize: 13, color: "#444", marginTop: 6, whiteSpace: "pre-wrap" }}>{i.resumen}</div>}
                </div>
              ))
            )}
          </Section>
        </>
      )}

      {/* ═══ CARA B: ficha de info + hitos ═══ */}
      {!esCaraA && (
        <>
          {/* Ficha "Lo que sé de ellos" */}
          <div style={{ ...S.cabecera, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#121212", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                ✱ Lo que sé de ellos
              </h3>
              <button onClick={() => setModalFicha(true)} style={S.btnLink}>✎ Editar ficha</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <FichaField label="Con quién hablo"      value={prospecto.ficha_con_quien_hablo} />
              <FichaField label="Qué les interesa"     value={prospecto.ficha_que_les_interesa} />
              <FichaField label="A quién le compran"   value={prospecto.ficha_a_quien_le_compran} />
              <FichaField label="Oportunidad"          value={prospecto.ficha_oportunidad} />
            </div>
          </div>

          {/* Acciones de Cara B */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {estado === "en_dialogo" && (
              <>
                <button onClick={() => setModalSolicitud(true)} style={{ ...S.btnPrimary, background: "#1d4ed8" }}>
                  ✉ Generar solicitud de cotización
                </button>
                <button onClick={() => setModalHito(true)}             style={S.btnSecondary}>✱ Anotar hito</button>
                <button onClick={() => setModalCambio("en_pausa")}     style={S.btnSecondary}>⏸ Quedó en pausa</button>
              </>
            )}
            {estado === "en_pausa" && (
              <>
                <button onClick={() => setModalCambio("en_dialogo")} style={S.btnPrimary}>▶ Reactivar a En diálogo</button>
                <button onClick={() => setModalHito(true)}           style={S.btnSecondary}>✱ Anotar hito</button>
              </>
            )}
            <button onClick={() => setModalTarea(true)} style={S.btnSecondary}>📅 Crear tarea</button>
          </div>

          {/* Timeline de hitos */}
          <Section title={`Hitos (${hitos.length})`}>
            {hitos.length === 0 ? (
              <p style={S.empty}>Sin hitos registrados todavía.</p>
            ) : (
              hitos.map((i) => (
                <div key={i.id} style={S.item}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1d4ed8" }}>
                      ✱ Hito
                      {i.canal && <span style={{ color: "#888", marginLeft: 8, fontWeight: 400 }}>· {CANAL_LABEL[i.canal] || i.canal}</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {i.usuario_nombre && <span style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 600 }}>{i.usuario_nombre}</span>}
                      <span style={{ fontSize: 11, color: "#888" }}>{formatFechaHora(i.fecha)}</span>
                    </div>
                  </div>
                  {i.contacto_nombre && (
                    <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                      Con: <strong>{i.contacto_nombre}</strong>
                    </div>
                  )}
                  {i.resumen && <div style={{ fontSize: 13, color: "#444", marginTop: 6, whiteSpace: "pre-wrap" }}>{i.resumen}</div>}
                  {i.proximo_paso && (
                    <div style={{ fontSize: 12, color: "#1d4ed8", marginTop: 6, padding: "6px 10px", background: "#eff6ff", borderRadius: 6, borderLeft: "3px solid #1d4ed8" }}>
                      → <strong>Próximo paso:</strong> {i.proximo_paso}
                    </div>
                  )}
                  {i.etapa_anterior && i.etapa_nueva && i.etapa_anterior !== i.etapa_nueva && (
                    <div style={{ fontSize: 11, color: "#9f0059", marginTop: 4, fontStyle: "italic" }}>
                      Estado: {ESTADO_LABEL[i.etapa_anterior] || i.etapa_anterior} → {ESTADO_LABEL[i.etapa_nueva] || i.etapa_nueva}
                    </div>
                  )}
                </div>
              ))
            )}
          </Section>
        </>
      )}

      {/* Contactos (común a las dos caras) */}
      <Section title={`Contactos (${contactos.length})`} action={
        <button onClick={() => setModalContacto({})} style={S.btnLink}>+ Agregar contacto</button>
      }>
        {contactos.length === 0 ? (
          <p style={S.empty}>Sin contactos registrados.</p>
        ) : (
          contactos.map((c) => (
            <div key={c.id} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "10px 14px", background: "#fafafa", borderRadius: 8,
              border: "1px solid #f0e6ec", marginBottom: 6,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>
                  {c.es_principal == 1 || c.es_principal === "1" ? "⭐ " : ""}{c.nombre}
                  {c.cargo && <span style={{ color: "#888", fontWeight: 400 }}> · {c.cargo}</span>}
                </div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                  {c.email    && <span style={{ marginRight: 12 }}>📧 {c.email}</span>}
                  {c.telefono && <span style={{ marginRight: 12 }}>📞 {c.telefono}</span>}
                  {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noreferrer" style={{ color: "#0a66c2" }}>💼 LinkedIn</a>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setModalContacto(c)} style={S.btnSmall}>Editar</button>
                <button onClick={() => eliminarContacto(c.id)} style={{ ...S.btnSmall, color: "#dc2626" }}>Eliminar</button>
              </div>
            </div>
          ))
        )}
      </Section>

      {/* Footer global: descartar siempre disponible */}
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "20px 0", borderTop: "1.5px solid #f0e6ec", marginTop: 16 }}>
        <button onClick={() => setModalDescart(true)} style={{ ...S.btnSecondary, color: "#dc2626", borderColor: "#dc2626" }}>
          🗑 Descartar prospecto
        </button>
      </div>

      {/* ═══ Modales ═══ */}
      {modalToque && (
        <ProspeccionToqueModal
          prospectoId={id}
          contactos={contactos}
          userId={userId}
          tipo={modalToque}
          titulo={modalToque === "respuesta" ? "Registrar respuesta" : (estado === "sin_tocar" ? "Registrar primer toque" : "Registrar toque")}
          onClose={() => setModalToque(null)}
          onSaved={() => { setModalToque(null); fetchDetalle(); }}
        />
      )}
      {modalHito && (
        <ProspeccionHitoModal prospectoId={id} contactos={contactos} userId={userId}
          onClose={() => setModalHito(false)} onSaved={() => { setModalHito(false); fetchDetalle(); }} />
      )}
      {modalFicha && (
        <ProspeccionFichaModal prospectoId={id} initial={prospecto}
          onClose={() => setModalFicha(false)} onSaved={() => { setModalFicha(false); fetchDetalle(); }} />
      )}
      {modalCambio && (
        <ProspeccionCambioEstadoModal
          prospectoId={id} estadoActual={estado} nuevoEstado={modalCambio} userId={userId}
          onClose={() => setModalCambio(null)}
          onSaved={() => { setModalCambio(null); fetchDetalle(); }}
        />
      )}
      {modalSolicitud && (
        <ProspeccionGenerarSolicitudModal
          prospecto={prospecto} contactos={contactos} userId={userId}
          onClose={() => setModalSolicitud(false)}
        />
      )}
      {modalDescart && (
        <ProspeccionDescartarModal
          prospectoId={id}
          onClose={() => setModalDescart(false)}
          onSaved={() => { setModalDescart(false); history.push("/dashboard/crm/prospeccion"); }}
        />
      )}
      {modalContacto !== null && (
        <ProspeccionContactoModal
          prospectoId={id} contacto={modalContacto}
          onClose={() => setModalContacto(null)}
          onSaved={() => { setModalContacto(null); fetchDetalle(); }}
        />
      )}
      {modalEditar && (
        <ProspeccionEditarModal
          prospecto={prospecto}
          onClose={() => setModalEditar(false)}
          onSaved={() => { setModalEditar(false); fetchDetalle(); }}
        />
      )}
      {modalTarea && (
        <ProspeccionTareaModal
          empresa={prospecto.empresa}
          userId={userId}
          onClose={() => setModalTarea(false)}
        />
      )}
    </div>
    </div>
  );
};

// ─── Auxiliares ──────────────────────────────────────────────────────────────
const Field = ({ label, value }) => (
  <div>
    <div style={S.miniLabel}>{label}</div>
    <div style={{ fontSize: 13, color: "#222", fontWeight: 500 }}>{value || "—"}</div>
  </div>
);

const FichaField = ({ label, value }) => (
  <div>
    <div style={S.miniLabel}>{label}</div>
    <div style={{ fontSize: 13, color: value ? "#222" : "#bbb", fontStyle: value ? "normal" : "italic", whiteSpace: "pre-wrap" }}>
      {value || "Sin definir"}
    </div>
  </div>
);

const Section = ({ title, action, children }) => (
  <div style={S.cabecera}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#121212", textTransform: "uppercase", letterSpacing: "0.05em" }}>{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

// ─── Estilos ──────────────────────────────────────────────────────────────────
const S = {
  cabecera: {
    background: "#fff", border: "1.5px solid #f0e6ec", borderRadius: 12,
    padding: "20px 24px", marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
  },
  miniLabel: {
    fontSize: 10, fontWeight: 700, color: "#888",
    textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3,
  },
  empty: { color: "#aaa", fontSize: 13, margin: 0 },
  item: {
    padding: "12px 14px", background: "#fafafa", borderRadius: 8,
    border: "1px solid #f0e6ec", marginBottom: 8,
  },
  btnPrimary: {
    padding: "10px 18px", background: "#9f0059", color: "#fff",
    border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  btnSecondary: {
    padding: "10px 18px", background: "#fff", color: "#9f0059",
    border: "1.5px solid #9f0059", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  btnPrimaryAmber: {
    padding: "8px 14px", background: "#92400e", color: "#fff",
    border: "1px solid #92400e", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer",
  },
  btnSecondaryAmber: {
    padding: "8px 14px", background: "#fff", color: "#92400e",
    border: "1px solid #92400e", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer",
  },
  btnLink: { background: "none", border: "none", color: "#9f0059", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnSmall: { background: "#fff", border: "1px solid #e0d6dc", borderRadius: 5, padding: "3px 8px", fontSize: 11, color: "#9f0059", fontWeight: 600, cursor: "pointer" },
};

export default ProspeccionDetalle;
